import {
  PortableTextComponentProps,
  PortableTextTypeComponent
} from '@portabletext/react'
import { ImageProps as NextImageProps } from 'next/image'
import { useEffect, useState } from 'react'

import { ParagraphNoSpace } from 'src/components/atoms/Typography'
import { CtaBlockResult } from 'src/components/layouts/Page/SanityContentPage/types'
import {
  CmsCtaButton,
  CmsCtaButtonProps
} from 'src/components/molecules/CmsCtaButton'
import { ContextActionBanner } from 'src/components/molecules/ContextActionBanner'

import { InternalLink } from 'src/config/generated/sanitySchema'
import { getSanityImageUrl } from 'src/utils/sanity'

import { BodyElement } from '../types'

const DEFAULT_IMG_SRC = '/img/default.png'

type ActionBlockProps = Omit<CtaBlockResult, '_type' | 'link'> & {
  link: CmsCtaButtonProps
}

export const ActionBlock = ({
  title,
  backgroundImage,
  intro,
  link
}: ActionBlockProps) => {
  const [imgSrc, setImgSrc] = useState<NextImageProps['src']>()
  useEffect(() => {
    try {
      if (!backgroundImage) setImgSrc(DEFAULT_IMG_SRC)
      else {
        const url = getSanityImageUrl(backgroundImage, 800, 240)
        setImgSrc(url)
      }
    } catch (error) {
      setImgSrc(DEFAULT_IMG_SRC)
    }
  }, [backgroundImage])

  return (
    <ContextActionBanner
      className='z-0 my-5 md:my-10 relative'
      backgroundSource={imgSrc as string}
      title={title}
      description={
        <ParagraphNoSpace className='!mb-[10px] !text-[16px] !leading!-[24px] whitespace-pre-line'>
          {intro}
        </ParagraphNoSpace>
      }
      cta={<CmsCtaButton className='md:self-start' size='large' {...link} />}
    />
  )
}

export const CtaBlock: PortableTextTypeComponent<BodyElement> = ({ value }) => {
  if (!value) return null
  const { backgroundImage, intro, title, link } = value as CtaBlockResult

  return (
    <ActionBlock
      backgroundImage={backgroundImage}
      intro={intro}
      title={title}
      link={link}
    />
  )
}

type OldCtaBlockResult = Omit<CtaBlockResult, 'link'> & {
  link: {
    text: string
    url: string
  }
}

export const CtaBlockOld = ({
  value
}: PortableTextComponentProps<ActionBlockProps>) => {
  if (!value) return null
  const { backgroundImage, intro, title, link } =
    value as unknown as OldCtaBlockResult

  return (
    <ActionBlock
      backgroundImage={backgroundImage}
      intro={intro}
      title={title}
      link={{
        text: link.text,
        linkType: 'internal',
        internalLink: {
          _type: 'internalLink',
          path: link.url
        } as InternalLink
      }}
    />
  )
}
